import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, gql } from '@apollo/client';
import { MainContext } from '@blaze-cms/nextjs-components';
import { getSingleEntitySchema, generateSingleItemQuery } from '../../application/query';
import ContentGroupTabs from './ContentGroupTabs';
import ContentGroupAccordion from './ContentGroupAccordion';
import { buildDataSections } from './helpers';
import { CONTENT_GROUP_TYPES } from './constants';

const PLACEHOLDER_QUERY = gql`
  query {
    __typename
  }
`;

const ContentGroupDataFetcher = ({ dataProperty, parent, contentType, ...props }) => {
  const { isPreview } = useContext(MainContext);
  const dataPropertyName = Array.isArray(dataProperty) ? dataProperty[0] : dataProperty;

  const { data: schemaData, loading: schemaLoading } = useQuery(getSingleEntitySchema, {
    variables: { id: parent?.itemEntity },
    skip: !parent?.itemEntity || !dataPropertyName
  });

  const dataQuery = useMemo(() => {
    if (!schemaData) return null;
    const { getEntitySchema } = schemaData;
    const { actions: { getPublished, get } = {} } = getEntitySchema;
    const actionKey = isPreview ? get : getPublished || get;
    if (!actionKey) return null;
    return generateSingleItemQuery(actionKey, `${dataPropertyName} { label text }`);
  }, [schemaData, isPreview, dataPropertyName]);

  const { data: recordData, loading: recordLoading } = useQuery(dataQuery || PLACEHOLDER_QUERY, {
    variables: { id: parent?.itemId },
    skip: !dataQuery || !parent?.itemId
  });

  if (schemaLoading || recordLoading) return '';
  if (!recordData?.entityData) return null;

  const propertyData = recordData.entityData[dataPropertyName];
  if (!propertyData || !propertyData.length) return null;

  const { groupSections, sectionsData } = buildDataSections(propertyData);

  if (!groupSections.length) return null;

  const GroupComponent =
    contentType === CONTENT_GROUP_TYPES.ACCORDION ? ContentGroupAccordion : ContentGroupTabs;

  return (
    <GroupComponent
      contentType={contentType}
      groupSections={groupSections}
      sectionsData={sectionsData}
      {...props}
    />
  );
};

ContentGroupDataFetcher.propTypes = {
  dataProperty: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  parent: PropTypes.object,
  contentType: PropTypes.string.isRequired
};

ContentGroupDataFetcher.defaultProps = {
  dataProperty: null,
  parent: null
};

export default ContentGroupDataFetcher;
